import React, { useState, useReducer, useEffect, Suspense, lazy, useContext } from "react";

import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, UNSAFE_DataRouterStateContext, useLocation, Outlet, useNavigate, Navigate } from "react-router-dom";
import { PostHogProvider } from 'posthog-js/react';

import { pathToRegexp } from "path-to-regexp";

import { useImmerReducer } from "use-immer";
import i18n from "./i18n";
import { isBrandTrackerHidden } from "./utils/featureFlags";

import StateContext from "./StateContext";
import DispatchContext from "./DispatchContext";
import LoadingDotsIcon from "./components/LoadingDotsIcon";
import tokenRefreshManager from "./utils/tokenRefreshManager";
import { getLanguageCode } from "./utils/languages";

// Lazy-loaded components
const Header = lazy(() => import("./components/Header"));
const AuthOverlay = lazy(() => import("./components/AuthOverlay"));
const SimpleHomeGuest = lazy(() => import("./components/SimpleHomeGuest"));
const ConnectMcp = lazy(() => import("./components/ConnectMcp"));
const Footer = lazy(() => import("./components/Footer"));
const About = lazy(() => import("./components/About"));
const MobileMessage = lazy(() => import("./components/MobileMessage"));
const Terms = lazy(() => import("./components/Terms"));
const EstatJapan = lazy(() => import("./components/EstatJapan"));
const GoogleMapsReviews = lazy(() => import("./components/GoogleMapsReviews"));
const Integrations = lazy(() => import("./components/Integrations"));
const DeepResearch = lazy(() => import("./components/DeepResearch"));
const TwitterIntegration = lazy(() => import("./components/TwitterIntegration"));
const RedditIntegration = lazy(() => import("./components/RedditIntegration"));
const AppReviewsIntegration = lazy(() => import("./components/AppReviewsIntegration"));
const TikTokIntegration = lazy(() => import("./components/TikTokIntegration"));
const InstagramIntegration = lazy(() => import("./components/InstagramIntegration"));
const TokopediaIntegration = lazy(() => import("./components/TokopediaIntegration"));
const ShopeeIntegration = lazy(() => import("./components/ShopeeIntegration"));
const LinkedInIntegration = lazy(() => import("./components/LinkedInIntegration"));
const NoteIntegration = lazy(() => import("./components/NoteIntegration"));
const AutobaseManager = lazy(() => import("./components/AutobaseManager"));
const GoogleAdsLibrary = lazy(() => import("./components/GoogleAdsLibrary"));
const MetaAdLibrary = lazy(() => import("./components/MetaAdLibrary"));
const AutobaseWizard = lazy(() => import("./components/AutobaseWizard"));
const Home = lazy(() => import("./components/Home"));
const CreatePost = lazy(() => import("./components/CreatePost"));
const ViewSinglePost = lazy(() => import("./components/ViewSinglePost"));
const FlashMessages = lazy(() => import("./components/FlashMessages"));
const Profile = lazy(() => import("./components/Profile"));
const EditPost = lazy(() => import("./components/EditPost"));
const NotFound = lazy(() => import("./components/NotFound"));
const EditResponse = lazy(() => import("./components/EditResponse"));
const Search = lazy(() => import("./components/Search"));
const StartMixer = lazy(() => import("./components/StartMixer"));
const FillSurveySingle = lazy(() => import("./components/FillSurveySingle"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const MySideNav = lazy(() => import("./components/MySideNav"));
const GEMSideNav = lazy(() => import("./components/GEMSideNav"));
const SelectModel = lazy(() => import("./components/SelectModel"));
const ModelDetails = lazy(() => import("./components/ModelDetails"));
const ModelSetup = lazy(() => import("./components/ModelSetup"));
const ModelDash = lazy(() => import("./components/ModelDash"));
const KnowledgeBaseDash = lazy(() => import("./components/KnowledgeBaseDash"));
const KnowledgeBaseQuery = lazy(() => import("./components/KnowledgeBaseQuery"));
const KnowledgeBaseView = lazy(() => import("./components/KnowledgeBaseView"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const ModelSettings = lazy(() => import("./components/ModelSettings"));
const ModelResponses = lazy(() => import("./components/ModelResponses"));
const ModelDetailCreator = lazy(() => import("./components/ModelDetailCreator"));
const GEMAnalysis = lazy(() => import("./components/report001/GEM-Analysis"));
const SERVPERFAnalysis = lazy(() => import("./components/report001/SERVPERF-Analysis"));
const GlobalSettings = lazy(() => import("./components/GlobalSettings"));
const AccessKnowledgeBase = lazy(() => import("./components/AccessKnowledgeBase"));

const GEMAnalysis1 = lazy(() => import("./components/report001/GEM-Analysis_One"));
const HeaderMobile = lazy(() => import("./components/HeaderMobile"));
const Landing = lazy(() => import("./components/Landing"));
const FooterLanding = lazy(() => import("./components/FooterLanding"));
const LandingGEM = lazy(() => import("./components/LandingGEM"));
const VerifyEmail = lazy(() => import("./components/VerifyEmail"));
const RequestResetPassword = lazy(() => import("./components/RequestResetPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));

const NewSurvey = lazy(() => import("./components/NewSurvey"));
const GoogleForms = lazy(() => import("./components/GoogleForms"));
const NewDash = lazy(() => import("./components/NewDash"));
const NewResponses = lazy(() => import("./components/NewResponses"));
const NewDashboard = lazy(() => import("./components/NewDashboard"));
const Customize = lazy(() => import("./components/Customize"));
const BrandTrackerWizard = lazy(() => import("./components/BrandTrackerWizard"));
const BrandTrackerDash = lazy(() => import("./components/BrandTrackerDash"));
const BrandTrackerLibrary = lazy(() => import("./components/BrandTrackerLibrary"));
const SocialListeningWizard = lazy(() => import("./components/SocialListeningWizard"));
const SocialListeningDash = lazy(() => import("./components/SocialListeningDash"));
const SocialListeningLibrary = lazy(() => import("./components/SocialListeningLibrary"));
const SurveyPlayground = lazy(() => import("./components/SurveyPlayground"));

const ViewFeedbacks = lazy(() => import("./components/ViewFeedbacks"));
const ViewFeedbacksOpenAI = lazy(() => import("./components/ViewFeedbacksOpenAI"));
// const NewTestSurvey = lazy(() => import("./components/NewTestSurvey"));
// const MinimalSurveyComponent = lazy(() => import("./components/MinimalSurveyComponent"));
const OpenVisuals = lazy(() => import("./components/OpenVisuals"));
const StartPoint = lazy(() => import("./components/StartPoint"));
const StartPointWrapper = lazy(() => import("./components/StartPointWrapper"));
const ResearchQuestionGenerator = lazy(() => import("./components/ResearchQuestionGenerator"));
const ResearchQuestionLibrary = lazy(() => import("./components/ResearchQuestionLibrary"));
const MergedSurveyForm = lazy(() => import("./components/MergedSurveyForm"));
const FrameworkSelectionPage = lazy(() => import("./components/FrameworkSelectionPage"));
const TheoryBridge = lazy(() => import("./components/TheoryBridge"));
const TheoryBridgeCoach = lazy(() => import("./components/TheoryBridgeCoach"));

import { CSSTransition } from "react-transition-group";

import axios from "axios";
const EditSurvey = lazy(() => import("./components/EditSurvey"));
const FormPlayground = lazy(() => import("./components/FormPlayground"));
const ResponsePlayground = lazy(() => import("./components/ResponsePlayground"));
const TablePlayground = lazy(() => import("./components/TablePlayground"));
const SurveyAnalysisCard = lazy(() => import("./components/SurveyAnalysisCard"));
const FormDemo = lazy(() => import("./components/FormDemo"));
const URLInputPage = lazy(() => import("./components/URLInputPage"));
const QuickStartInput = lazy(() => import("./components/QuickStartInput"));
const QuickStartStatus = lazy(() => import("./components/QuickStartStatus"));
const QuickStartResults = lazy(() => import("./components/QuickStartResults"));
const QuickStartLibrary = lazy(() => import("./components/QuickStartLibrary"));
const QuickDemo = lazy(() => import("./components/QuickDemo"));
const QuickDemoStatus = lazy(() => import("./components/QuickDemoStatus"));
const QuickDemoResults = lazy(() => import("./components/QuickDemoResults"));
const DemoFormPage = lazy(() => import("./components/DemoFormPage"));
const SelectProduct = lazy(() => import("./components/SelectProduct"));
const EditMagicSurvey = lazy(() => import("./components/EditMagicSurvey"));
const URLInputPageLoggedIn = lazy(() => import("./components/URLInputPageLoggedIn"));
const MagicFormPage = lazy(() => import("./components/MagicFormPage"));
const HomeSelect = lazy(() => import("./components/HomeSelect"));
const SurveySender = lazy(() => import("./components/SurveySender"));

const SimpleLoginPage = lazy(() => import("./components/SimpleLoginPage"));
const LanguageSelector = lazy(() => import("./components/LanguageSelector"));
const MediaManager = lazy(() => import("./components/MediaManager"));
const SelectOption = lazy(() => import("./components/SelectOption"));
const ExternalPlatforms = lazy(() => import("./components/ExternalPlatforms"));
const TallyIntegration = lazy(() => import("./components/TallyIntegration"));
const CreateHookSurvey = lazy(() => import("./components/CreateHookSurvey"));
const SelectSwOption = lazy(() => import("./components/SelectSwOption"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const CognitoAuthCallback = lazy(() => import("./components/CognitoAuthCallback"));
const SelectAnalysisOption = lazy(() => import("./components/SelectAnalysisOption"));
const DocumentUpload = lazy(() => import("./components/DocumentUpload"));
const DocumentLibrary = lazy(() => import("./components/DocumentLibrary"));
const ExcelUpload = lazy(() => import("./components/ExcelUpload"));
const PublicDash = lazy(() => import("./components/PublicDash"));

const TestSurvey = lazy(() => import("./components/TestSurvey"));
const PricingPlans = lazy(() => import("./components/PricingPlans"));
const UsagePage = lazy(() => import("./components/UsagePage"));
const TokenAnalyticsDashboard = lazy(() => import("./components/TokenAnalyticsDashboard"));
const PersonaLibrary = lazy(() => import("./components/PersonaLibrary"));
const PersonaCollectionDash = lazy(() => import("./components/PersonaCollectionDash"));
const PersonaChatLauncher = lazy(() => import("./components/PersonaChatLauncher"));
const PersonaChat = lazy(() => import("./components/PersonaChat"));
const PersonaChatAnalysis = lazy(() => import("./components/PersonaChatAnalysis"));
const InterviewLibrary = lazy(() => import("./components/InterviewLibrary"));
const InterviewDash = lazy(() => import("./components/InterviewDash"));
const YataFormLibrary = lazy(() => import("./components/YataFormLibrary"));
const Teams = lazy(() => import("./components/Teams"));
const Organizations = lazy(() => import("./components/Organizations"));
const Projects = lazy(() => import("./components/Projects"));
const ProjectDetails = lazy(() => import("./components/ProjectDetails"));
const AuditLogViewer = lazy(() => import("./components/AuditLogViewer"));
const GoogleSSOTest = lazy(() => import("./components/GoogleSSOTest"));
const GoogleSSOTestPage = lazy(() => import("./components/GoogleSSOTestPage"));
const AmplifyOAuthCallback = lazy(() => import("./components/AmplifyOAuthCallback"));

import GradioInterface from "./components/GradioInterface";
import "./main.css";
import "./yatabase-theme.css";
import URLInputPageBeta from "./components/URLInputPageBeta";
import axiosInstance from "./utils/axiosConfig";

// Initialize Cognito configuration
import "./utils/cognitoConfig";
import cognitoAuth from "./utils/cognitoAuth";
import { logout } from "./utils/logout";

// Set Axios base URL based on environment
const backendUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL_PROD : process.env.REACT_APP_BACKEND_URL_DEV;

axios.defaults.baseURL = backendUrl;
axiosInstance.defaults.baseURL = backendUrl;

console.log("default base", axios.defaults.baseURL);

// Layout and route components defined at module scope so their identity is stable
// across re-renders of Main. If these are defined inside Main(), React sees a new
// component type on every render and unmounts/remounts the entire route tree.

function ProtectedRoute({ children }) {
  const state = useContext(StateContext);
  const { loggedIn } = state;
  const location = useLocation();

  console.log("ProtectedRoute - loggedIn:", loggedIn);
  console.log("ProtectedRoute - location:", location.pathname);

  return loggedIn === true ? (
    children
  ) : (
    <Navigate
      to="/start-point"
      state={{ from: location.pathname }}
      replace
    />
  );
}

const ReportLayout = () => {
  return (
    <>
      <Header />
      <Outlet />
    </>
  );
};

const MetaLayout = () => {
  return (
    <>
      <Header />
      <MySideNav />
      <Outlet />
    </>
  );
};

const FreeLayoutX = () => {
  const isMobile = window.innerWidth <= 767;

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh" }}>
          <MySideNav />
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Header />
            <Outlet />
          </div>
        </div>
      )}
    </>
  );
};

function FreeLayout() {
  const { loggedIn } = useContext(StateContext);
  const [showOverlay, setShowOverlay] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile = window.innerWidth <= 767;

  useEffect(() => {
    setShowOverlay(!loggedIn);
  }, [loggedIn]);

  const closeOverlay = () => {
    setShowOverlay(false);
    if (!loggedIn) {
      navigate("/start-point");
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh" }}>
          <MySideNav />
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Header />
            {loggedIn && <Outlet />}
          </div>
        </div>
      )}

      <AuthOverlay
        isOpen={showOverlay}
        onClose={closeOverlay}
        redirectPath={location.pathname}
      />
    </>
  );
}

function FreeLayoutNoSidebar() {
  const { loggedIn } = useContext(StateContext);
  const [showOverlay, setShowOverlay] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile = window.innerWidth <= 767;

  useEffect(() => {
    setShowOverlay(!loggedIn);
  }, [loggedIn]);

  const closeOverlay = () => {
    setShowOverlay(false);
    if (!loggedIn) {
      navigate("/start-point");
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh" }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Header />
            {loggedIn && <Outlet />}
          </div>
        </div>
      )}

      <AuthOverlay
        isOpen={showOverlay}
        onClose={closeOverlay}
        redirectPath={location.pathname}
      />
    </>
  );
}

function FreeLayoutNoSidebarNoHeader() {
  const { loggedIn } = useContext(StateContext);
  const [showOverlay, setShowOverlay] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile = window.innerWidth <= 767;

  useEffect(() => {
    setShowOverlay(!loggedIn);
  }, [loggedIn]);

  const closeOverlay = () => {
    setShowOverlay(false);
    if (!loggedIn) {
      navigate("/start-point");
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh" }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loggedIn && <Outlet />}
          </div>
        </div>
      )}

      <AuthOverlay
        isOpen={showOverlay}
        onClose={closeOverlay}
        redirectPath={location.pathname}
      />
    </>
  );
}

const LandingLayout = () => {
  const isMobile = window.innerWidth <= 767;
  return (
    <>
      {!isMobile && <Header />}
      {isMobile && <HeaderMobile />}
      <Outlet />
    </>
  );
};

const NoLayout = () => {
  const isMobile = window.innerWidth <= 767;

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh" }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Outlet />
          </div>
        </div>
      )}
    </>
  );
};

const StartLayout = () => {
  const isMobile = window.innerWidth <= 767;
  const { loggedIn } = useContext(StateContext);

  return (
    <>
      {isMobile ? (
        <MobileMessage />
      ) : (
        <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
          {loggedIn && <MySideNav />}
          <div style={{ flex: 1, overflow: loggedIn ? "auto" : "hidden" }}>
            {loggedIn && <Header />}
            <Outlet />
          </div>
        </div>
      )}
    </>
  );
};

function Main() {
  const mode = "development";
  mode === "production" && (console.log = () => {});

  // Helper function to safely get sessionStorage values with validation
  const getSafeSessionStorage = (key, defaultValue = null) => {
    try {
      // First try sessionStorage (tab-specific)
      let value = sessionStorage.getItem(key);
      
      // Fallback to localStorage for cross-tab persistence
      if (!value || value === 'null' || value === 'undefined') {
        value = localStorage.getItem(key);
      }
      
      if (value === null || value === 'null' || value === 'undefined') {
        return defaultValue;
      }
      return value;
    } catch (error) {
      console.warn(`Error accessing storage for key ${key}:`, error);
      return defaultValue;
    }
  };

  // Helper to store in both storages for persistence
  const setSafeStorage = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value); // Also store in localStorage for cross-tab
    } catch (error) {
      console.warn(`Error setting storage for key ${key}:`, error);
    }
  };

  // Helper to clear both storages
  const clearSafeStorage = (key) => {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Error clearing storage for key ${key}:`, error);
    }
  };

  // Validate token format (basic check for JWT structure)
  const isValidToken = (token) => {
    if (!token || typeof token !== 'string') return false;
    if (token === 'null' || token === 'undefined') return false;
    if (token.length < 20) return false;
    // Check for JWT structure (three parts separated by dots)
    const parts = token.split('.');
    return parts.length === 3;
  };

  // Lazy initializer — only runs on first mount, not on re-renders
  const createInitialState = () => {
    const token = getSafeSessionStorage("complexappToken");
    const pythanksClientId = getSafeSessionStorage("pythanksClientId");

    console.log("🔄 Main.js initializing with auth state:", {
      hasToken: !!token,
      tokenValid: isValidToken(token),
      hasClientId: !!pythanksClientId,
      tokenLength: token?.length,
      clientIdLength: pythanksClientId?.length
    });

    const getUIPermissions = () => {
      try {
        const stored = getSafeSessionStorage("uiPermissions");
        if (!stored || stored === 'undefined' || stored === 'null') {
          return {};
        }
        return JSON.parse(stored);
      } catch (e) {
        console.error("Failed to parse UI permissions:", e);
        return {};
      }
    };

    const state = {
      loggedIn: isValidToken(token) && !!pythanksClientId,
      flashMessages: [],
      user: {
        token: isValidToken(token) ? token : null,
        userId: getSafeSessionStorage("userId"),
        _id: getSafeSessionStorage("userId") || getSafeSessionStorage("pythanksClientId"),
        username: getSafeSessionStorage("complexappUsername"),
        pythanksClientId: pythanksClientId,
        email: getSafeSessionStorage("email"),
        emailValidated: getSafeSessionStorage("emailValidated"),
        plan: getSafeSessionStorage("plan", "free"),
        language: getSafeSessionStorage("currentLanguage", "en"),
        uiPermissions: getUIPermissions(),
      },
      surveys: [],
      isSearchOpen: false,
      showHeader: true,
      activeSurveyId: null,
      activeMenuItem: "analysis",
    };

    console.log("🔄 Initial state created:", {
      loggedIn: state.loggedIn,
      hasUserToken: !!state.user.token,
      userId: state.user.userId,
      hasClientId: !!state.user.pythanksClientId,
      _id: state.user._id
    });

    return state;
  };

  const ourReducer = (draft, action) => {
    switch (action.type) {
      case "login":
        draft.loggedIn = true;
        draft.user = action.data;
        return;
      case "logout":
        draft.loggedIn = false;
        return;
      case "flashMessage":
        // draft.flashMessages.push(action.value);
        return;
      case "openSearch":
        draft.isSearchOpen = true;
        return;
      case "closeSearch":
        draft.isSearchOpen = false;
        return;
      case "setActiveSurveyId":
        draft.activeSurveyId = action.value;
        return;
      case "setActiveMenuItem":
        draft.activeMenuItem = action.value;
        return;
      case "setNewClient":
        draft.user.newClient = action.value;
        sessionStorage.setItem("newClient", action.value);
        return;
      case "setSurveys":
        draft.surveys = action.value;
        return;
      case "updateSurveyName":
        const survey = draft.surveys.find((s) => s._id === action.value.id);
        if (survey) {
          survey.settings.name = action.value.name;
        }
        return;
      case "updateUserLanguage":
        draft.user.language = action.value;
        sessionStorage.setItem("currentLanguage", action.value);
        return;
      case "updateUserContext":
        // Update user context for organization switching
        Object.assign(draft.user, action.data);
        return;
    }
  };

  const [state, dispatch] = useImmerReducer(ourReducer, null, createInitialState);

  useEffect(() => {
    const splash = document.getElementById("splash");
    if (splash) {
      splash.style.display = "none";
    }
    
    // Initialize token refresh manager on mount if token exists
    const existingToken = sessionStorage.getItem("complexappToken") || localStorage.getItem("complexappToken");
    if (existingToken) {
      console.log("🚀 Found existing token on mount, initializing refresh manager");
      tokenRefreshManager.initialize();
    }
    
    // Listen for session expired events
    const handleSessionExpired = async () => {
      console.log("⚠️ Session expired, logging out...");
      const { logout } = await import('./utils/logout');
      await logout(dispatch);
    };
    
    window.addEventListener('sessionExpired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (state.loggedIn) {
      console.log("see user: ", state.user);
      // Use the safe storage helper to persist across tabs
      setSafeStorage("complexappToken", state.user.token);
      setSafeStorage("complexappUsername", state.user.username);
      setSafeStorage("complexappAvatar", state.user.avatar);
      setSafeStorage("userId", state.user.userId || state.user._id); // Store Cognito user ID
      setSafeStorage("pythanksClientId", state.user.pythanksClientId);
      setSafeStorage("email", state.user.email);
      setSafeStorage("newClient", state.user.newClient);
      setSafeStorage("emailValidated", state.user.emailValidated);
      setSafeStorage("plan", state.user.plan);
      setSafeStorage("currentLanguage", state.user.language || "en");
      setSafeStorage("uiPermissions", JSON.stringify(state.user.uiPermissions || {}));
      
      // Initialize token refresh manager for Cognito tokens
      if (state.user.token) {
        console.log("🚀 Initializing token refresh manager for logged-in user");
        tokenRefreshManager.initialize();
      }
    } else {
      // Clear from both storages
      clearSafeStorage("complexappToken");
      clearSafeStorage("complexappUsername");
      clearSafeStorage("complexappAvatar");
      clearSafeStorage("userId");
      clearSafeStorage("pythanksClientId");
      clearSafeStorage("email");
      clearSafeStorage("newClient");
      clearSafeStorage("emailValidated");
      clearSafeStorage("surveyJson");
      clearSafeStorage("surveyCriteria");
      clearSafeStorage("surveyCompany");
      clearSafeStorage("surveyProblem");
      clearSafeStorage("uiPermissions");
      
      // Clean up token refresh manager
      tokenRefreshManager.cleanup();
    }
  }, [state.loggedIn]);

  // Sync i18next language with stored user preference
  useEffect(() => {
    // Check if language was set via URL parameter - don't override it
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lng');
    if (urlLang) {
      // URL parameter takes priority - don't override
      return;
    }

    // Check if user has an explicit localStorage preference (set on login page)
    // This takes priority over backend-stored language to respect user's recent choice
    const localStorageLang = localStorage.getItem('i18nextLng');
    if (localStorageLang && i18n.language === localStorageLang) {
      // localStorage preference matches current i18n language - no action needed
      // HeaderLoggedIn will sync this to backend
      return;
    }

    // Handle both old format (full names) and new format (language codes)
    if (state.user.language) {
      // Accepts both codes and legacy full names
      const langCode = getLanguageCode(state.user.language);

      // Only change language if localStorage doesn't have a different preference
      // This prevents overriding the user's login page selection
      if (localStorageLang && localStorageLang !== langCode) {
        // User selected a language on login page that differs from backend
        // Keep the localStorage preference - HeaderLoggedIn will sync to backend
        console.log('🌐 Preserving localStorage language preference:', localStorageLang, 'over backend:', langCode);
        return;
      }

      if (i18n.language !== langCode) {
        i18n.changeLanguage(langCode);
      }
    }
  }, [state.user.language]);

  // Handle tab visibility changes to refresh tokens
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && state.loggedIn) {
        console.log("🔍 Tab became visible, refreshing Cognito session...");
        try {
          const cognitoAuth = (await import('./utils/cognitoAuth')).default;
          const refreshResult = await cognitoAuth.refreshSession();
          
          if (refreshResult.success) {
            console.log("✅ Session refreshed on tab activation");
            // Update stored tokens
            if (refreshResult.idToken) {
              setSafeStorage("complexappToken", refreshResult.idToken);
            }
          } else {
            console.warn("⚠️ Failed to refresh session on tab activation");
          }
        } catch (error) {
          console.error("❌ Error refreshing session on visibility change:", error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also handle focus events for better reliability
    const handleFocus = () => {
      if (state.loggedIn) {
        handleVisibilityChange();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [state.loggedIn]);

  // Cognito monitoring for external signouts
  useEffect(() => {
    let authCheckInterval;
    
    // Remove userId requirement - make monitoring automatic for all Cognito users
    const MONITORING_DISABLED = false; // Always enable for logged-in users
    
    if (MONITORING_DISABLED) {
      console.log("🔍 Cognito monitoring disabled globally");
      return;
    }

    const checkCognitoAuthState = async () => {
      try {
        console.log("🔍 [MONITOR] Starting Cognito token validity check...");
        let isTokenValid = false;
        let tokenDetails = {};
        let validationError = null;
        let userIdMatch = true; // Default to true
        let serverUserId = null;
        let appUserId = state.user?.userId;

        console.log("🔍 [MONITOR] Current app state:", {
          loggedIn: state.loggedIn,
          hasUserId: !!appUserId,
          userIdValue: appUserId,
          email: state.user?.email
        });

        // Check for stale browser storage first
        const checkBrowserStorageHealth = () => {
          try {
            // Test if we can access storage
            const testKey = '_healthcheck_' + Date.now();
            sessionStorage.setItem(testKey, 'test');
            const testValue = sessionStorage.getItem(testKey);
            sessionStorage.removeItem(testKey);
            
            if (testValue !== 'test') {
              console.error("🔍 [MONITOR] Storage test failed - cannot read/write");
              return false;
            }
            
            // Check for corrupted Cognito storage and clean it up
            const cognitoKeys = Object.keys(localStorage).filter(key => 
              key.includes('CognitoIdentityServiceProvider')
            );
            
            console.log(`🔍 [MONITOR] Found ${cognitoKeys.length} Cognito storage keys`);
            
            let corruptedItemsFound = false;
            for (const key of cognitoKeys) {
              try {
                const value = localStorage.getItem(key);
                if (value && value !== 'undefined' && value !== 'null') {
                  // Only try to parse if it looks like JSON
                  if (value.startsWith('{') || value.startsWith('[') || value.startsWith('"')) {
                    JSON.parse(value); // Will throw if corrupted
                  }
                }
              } catch (e) {
                console.warn("🔍 [MONITOR] Corrupted Cognito storage detected, clearing:", key, "Error:", e.message);
                localStorage.removeItem(key);
                corruptedItemsFound = true;
              }
            }
            
            // Only return false if storage itself is inaccessible
            // Corrupted items are cleaned up but don't require logout
            if (corruptedItemsFound) {
              console.log("🔍 [MONITOR] Cleaned up corrupted storage items, continuing validation...");
            }
            
            console.log("🔍 [MONITOR] Storage health check passed");
            return true;
          } catch (error) {
            console.error("🔍 [MONITOR] Browser storage health check failed with exception:", error);
            // Only fail if we truly cannot access storage at all
            try {
              // Last ditch test
              localStorage.length; // This will throw if storage is completely broken
              return true; // Storage works, just had a minor issue
            } catch (e) {
              return false; // Storage is completely broken
            }
          }
        };

        const storageHealthy = checkBrowserStorageHealth();
        console.log("🔍 [MONITOR] Storage health check result:", storageHealthy);
        
        if (!storageHealthy) {
          console.log("⚠️ [MONITOR] Storage access failed completely, triggering logout");
          try {
            const { logout } = await import('./utils/logout');
            await logout(dispatch);
          } catch (logoutError) {
            console.error("❌ [MONITOR] Failed to logout:", logoutError);
          }
          return;
        }

        try {
          const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth');
          console.log("🔍 [MONITOR] Fetching auth session...");
          const session = await fetchAuthSession({ forceRefresh: true });
          
          // Extract token details for debugging
          const tokens = session.tokens;
          tokenDetails = {
            hasAccessToken: !!tokens?.accessToken,
            hasIdToken: !!tokens?.idToken,
            accessTokenExpiry: tokens?.accessToken?.payload?.exp ? new Date(tokens.accessToken.payload.exp * 1000).toISOString() : 'N/A',
            idTokenExpiry: tokens?.idToken?.payload?.exp ? new Date(tokens.idToken.payload.exp * 1000).toISOString() : 'N/A',
            locallyExpired: tokens?.accessToken?.payload?.exp ? Date.now() > (tokens.accessToken.payload.exp * 1000) : false,
          };

          console.log("🔍 [MONITOR] Token details:", tokenDetails);

          if (session.tokens?.accessToken && session.tokens?.idToken) {
            console.log("🔍 [MONITOR] Tokens exist locally, validating with Cognito server...");
            
            try {
              console.log("🔍 [MONITOR] Calling getCurrentUser()...");
              const user = await getCurrentUser();
              console.log("🔍 [MONITOR] getCurrentUser() success:", {
                userExists: !!user,
                userId: user?.userId || user?.sub,
                email: user?.attributes?.email || user?.email
              });
              
              isTokenValid = !!user;
              serverUserId = user?.userId || user?.sub;
              
              // If we don't have userId in state yet, fetch and store it automatically
              if (!appUserId && serverUserId) {
                console.log("🔄 [MONITOR] Auto-fetching and storing Cognito userId:", serverUserId);
                
                // Store in both storages for persistence
                setSafeStorage("userId", serverUserId);
                
                // Update state
                dispatch({
                  type: "updateUser",
                  data: {
                    ...state.user,
                    userId: serverUserId
                  }
                });
                
                appUserId = serverUserId;
                console.log("✅ [MONITOR] Cognito userId automatically stored:", serverUserId);
              }
              
              // Check userId match
              userIdMatch = !appUserId || (serverUserId === appUserId);
              
              console.log("🔍 [MONITOR] Server-side validation details:", {
                success: isTokenValid,
                userExists: !!user,
                serverUserId,
                appUserId,
                userIdMatch,
                expectedEmail: state.user?.email || 'no expected email',
                serverEmail: user?.attributes?.email || user?.email || 'no server email'
              });
              
              isTokenValid = isTokenValid && userIdMatch;
              
              if (!userIdMatch && isTokenValid) {
                console.log("⚠️ [MONITOR] User ID mismatch detected! Different user logged in.");
                isTokenValid = false;
              }
              
            } catch (userError) {
              console.log("🔍 [MONITOR] getCurrentUser() failed:", userError.message);
              console.log("🔍 [MONITOR] Error details:", userError);
              validationError = userError.message;
              isTokenValid = false;
            }
          } else {
            console.log("🔍 [MONITOR] No tokens found locally");
            isTokenValid = false;
          }
          
        } catch (tokenError) {
          console.log("🔍 [MONITOR] Token fetch failed:", tokenError.message);
          console.log("🔍 [MONITOR] Token error details:", tokenError);
          validationError = tokenError.message;
          isTokenValid = false;
        }

        console.log("🔍 [MONITOR] FINAL Auth state check:", {
          appLoggedIn: state.loggedIn,
          hasLocalTokens: tokenDetails.hasAccessToken && tokenDetails.hasIdToken,
          locallyExpired: tokenDetails.locallyExpired,
          serverValidated: isTokenValid,
          userIdMatch: userIdMatch,
          validationError,
          shouldLogout: state.loggedIn && !isTokenValid,
          tokenDetails
        });

        if (state.loggedIn && !isTokenValid) {
          console.log("🔄 [MONITOR] ⚠️ EXTERNAL LOGOUT DETECTED! Triggering logout...");
          console.log("🔄 [MONITOR] Reason:", validationError || 'Server validation failed');
          const { logout } = await import('./utils/logout');
          await logout(dispatch);
          console.log("✅ [MONITOR] Logout completed due to server-side validation failure");
        } else if (state.loggedIn && isTokenValid) {
          console.log("✅ [MONITOR] User still valid, continuing...");
        } else if (!state.loggedIn) {
          console.log("ℹ️ [MONITOR] User not logged in, skipping validation");
        }
        
      } catch (error) {
        console.error("❌ [MONITOR] Error checking Cognito token validity:", error);
      }
    };

    if (state.loggedIn) {
      console.log("🔍 [MONITOR] 🚀 Starting automatic Cognito server-side token validation monitoring (every 30 seconds)...");
      checkCognitoAuthState(); // Run immediately
      authCheckInterval = setInterval(checkCognitoAuthState, 30000); // 30 seconds interval (reduced frequency)
    } else {
      console.log("🔍 [MONITOR] User not logged in, skipping Cognito monitoring");
    }

    return () => {
      if (authCheckInterval) {
        console.log("🔍 [MONITOR] 🛑 Stopping Cognito token validation monitoring");
        clearInterval(authCheckInterval);
      }
    };
  }, [state.loggedIn]);

  const isMobile = window.innerWidth <= 767;

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        <BrowserRouter>
          {/* <Suspense fallback={<div>Loading...</div>}> */}
          <Suspense fallback={<LoadingDotsIcon />}>
            <FlashMessages messages={state.flashMessages} />
            <Routes>
              <Route element={<StartLayout />}>
                <Route path="/start-point" element={<StartPointWrapper />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Home />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route element={<FreeLayout />}>
                <Route path="/model-details/:surveytemplateid" element={<ModelDetails />} />
                <Route path="/model-setup/:surveytemplateid" element={<ModelSetup />} />
                <Route path="/model-responses/:surveyid" element={<NewResponses />} />
                <Route path="/model-dash/:surveyid/:tab?" element={<ModelDash />} />
                <Route path="/kb/:surveyid/manage" element={<KnowledgeBaseDash />} />
                <Route path="/kb/:surveyid" element={<KnowledgeBaseQuery />} />
                <Route path="/model-detail-creator/*" element={<ModelDetailCreator />} />
                <Route path="/global-settings/" element={<GlobalSettings />} />
                <Route path="/customize/:templateid" element={<Customize />} />
                {/* TEMP: hidden for Japanese users pending a native review of the
                    tracker's Japanese wording. Routes are gated too, not just the
                    nav entries, so a pasted URL cannot reach an unreviewed page —
                    these fall through to NotFound. See utils/featureFlags. */}
                {!isBrandTrackerHidden(i18n.language) && (
                  <>
                    <Route path="/brand-trackers" element={<BrandTrackerLibrary />} />
                    <Route path="/brand-tracker/new" element={<BrandTrackerWizard />} />
                    <Route path="/brand-tracker/:surveyid" element={<BrandTrackerDash />} />
                  </>
                )}
                <Route path="/social-listening" element={<SocialListeningLibrary />} />
                <Route path="/social-listening/new" element={<SocialListeningWizard />} />
                <Route path="/social-listening/:surveyid" element={<SocialListeningDash />} />
                <Route path="/edit-survey/:surveyid" element={<EditSurvey />} />
                <Route path="/view-feedback/:surveyid/:filter?" element={<ViewFeedbacks />} />
                <Route path="/survey-sender/:surveyid" element={<SurveySender />} />
                <Route path="/media-manager" element={<MediaManager />} />
                <Route path="/create-hook-survey" element={<CreateHookSurvey />} />
                <Route path="/select-analysis-option" element={<SelectAnalysisOption />} />
                <Route path="/knowledge-bases" element={<DocumentLibrary />} />
                <Route path="/document-upload" element={<DocumentUpload />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/deep-research" element={<DeepResearch />} />
                <Route path="/estat-japan" element={<EstatJapan />} />
                <Route path="/google-maps-reviews" element={<GoogleMapsReviews />} />
                <Route path="/twitter-integration" element={<TwitterIntegration />} />
                <Route path="/reddit-integration" element={<RedditIntegration />} />
                <Route path="/app-reviews-integration" element={<AppReviewsIntegration />} />
                <Route path="/tiktok-integration" element={<TikTokIntegration />} />
                <Route path="/instagram-integration" element={<InstagramIntegration />} />
                <Route path="/tokopedia-integration" element={<TokopediaIntegration />} />
                <Route path="/shopee-integration" element={<ShopeeIntegration />} />
                <Route path="/linkedin-integration" element={<LinkedInIntegration />} />
                <Route path="/note-integration" element={<NoteIntegration />} />
                <Route path="/google-ads-library" element={<GoogleAdsLibrary />} />
                <Route path="/meta-ads-library" element={<MetaAdLibrary />} />
                <Route path="/autobase" element={<AutobaseManager />} />
                <Route path="/autobase/wizard" element={<AutobaseWizard />} />
                {/* <Route path="/start-magic" element={<URLInputPage />} /> */}
                <Route path="/quickstart" element={<QuickStartInput />} />
                <Route path="/fast-research-library" element={<QuickStartLibrary />} />
                <Route path="/fast-research-status/:auditId" element={<QuickStartStatus />} />
                <Route path="/fast-research-results/:auditId" element={<QuickStartResults />} />
                <Route path="/select-sw-option" element={<SelectSwOption />} />
                <Route path="/select-model" element={<SelectModel />} />
                <Route path="/access-knowledge-base" element={<AccessKnowledgeBase />} />
                <Route path="/knowledge-base/:surveyid" element={<PublicDash />} />
                <Route path="/pricing" element={<PricingPlans />} />
                <Route path="/usage" element={<UsagePage />} />
                <Route path="/admin/token-analytics" element={<TokenAnalyticsDashboard />} />
                <Route path="/persona-library" element={<PersonaLibrary />} />
                <Route path="/persona-collection/:collectionId" element={<PersonaCollectionDash />} />
                <Route path="/persona-chat/launch/:collectionId" element={<PersonaChatLauncher />} />
                <Route path="/persona-chat/:sessionId/analysis" element={<PersonaChatAnalysis />} />
                <Route path="/persona-chat/:sessionId" element={<PersonaChat />} />
                <Route path="/interview-library" element={<InterviewLibrary />} />
                <Route path="/interview/:interviewId" element={<InterviewDash />} />
                <Route path="/research-questions/:id" element={<ResearchQuestionGenerator />} />
                <Route path="/research-questions-library" element={<ResearchQuestionLibrary />} />
                <Route path="/theory-bridge" element={<TheoryBridge />} />
                <Route path="/theory-bridge/:id" element={<TheoryBridge />} />
                <Route path="/theory-bridge/:id/coach" element={<TheoryBridgeCoach />} />
                <Route path="/yataform-library" element={<YataFormLibrary />} />
                      <Route path="/teams" element={<Teams />} />
              <Route path="/organizations" element={<Organizations />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/project-details/:projectId" element={<ProjectDetails />} />
        <Route path="/audit-logs" element={<AuditLogViewer />} />
              </Route>

              <Route element={<FreeLayoutNoSidebar />}>
                <Route path="/framework-selection" element={<FrameworkSelectionPage />} />
              </Route>

              <Route element={<LandingLayout />}>{/* <Route path="/landing" element={<LandingGEM />} /> */}</Route>
              <Route element={<FreeLayoutNoSidebarNoHeader />}>
                <Route path="/edit-magic-survey/:surveyid" element={<EditMagicSurvey />} />
                <Route path="/merged-survey-form" element={<MergedSurveyForm />} />
              </Route>

              <Route element={<NoLayout />}>
                <Route path="/about-us" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/quick" element={<QuickDemo />} />
                <Route path="/quick-demo-status/:auditId" element={<QuickDemoStatus />} />
                <Route path="/quick-demo-results/:auditId" element={<QuickDemoResults />} />

                <Route path="/select-product" element={<SelectProduct />} />
                <Route path="/register" element={<SimpleHomeGuest />} />
                <Route path="/connect-mcp" element={<ConnectMcp />} />
                <Route path="/validate-email/:token" element={<VerifyEmail />} />
                <Route path="/request-reset-password/" element={<RequestResetPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/test-survey" element={<TestSurvey />} />
                <Route path="/language-selector" element={<LanguageSelector />} />
                <Route path="/google-sso-test" element={<GoogleSSOTest />} />
                <Route path="/google-sso-auth-test" element={<GoogleSSOTestPage />} />
                {/* <Route path="/survey-playground" element={<SurveyPlayground />} />
                <Route path="/form-playground/:customer?" element={<FormPlayground />} />
                <Route path="/response-playground" element={<ResponsePlayground />} />
                <Route path="/table-playground" element={<TablePlayground />} />
                <Route path="/survey-analysis-card" element={<SurveyAnalysisCard />} />
                <Route path="/form-demo/:customer?" element={<FormDemo />} /> */}
                {/* <Route path="/start-magic" element={<URLInputPage />} /> */}
                {/* <Route path="/magic-survey" element={<DemoFormPage />} />

                <Route path="/magic-survey-form" element={<MagicFormPage />} />
                <Route path="/merged-survey-form" element={<MergedSurveyForm />} />
                <Route path="/start-magic-beta" element={<URLInputPageBeta />} />
                <Route path="/language-selector" element={<LanguageSelector />} /> */}

                {/* <Route path="/external-platforms" element={<ExternalPlatforms />} />
                <Route path="/tally-integration" element={<TallyIntegration />} />
                <Route path="/select-option" element={<SelectOption />} /> */}
              </Route>

              <Route element={<MetaLayout />}>
                <Route path="/sidenav" element={<MySideNav />} />
              </Route>

              <Route element={<ReportLayout />}>
                <Route path="/dashboard/:username/*" element={<Dashboard />} />
                <Route path="/model-settings/:surveyid" element={<ModelSettings />} />
                <Route path="/GEM/analysis1/:surveyid/:sectionid" element={<GEMAnalysis />} />
                <Route path="/SERVPERF/analysis/:surveyid/:sectionid" element={<SERVPERFAnalysis />} />
                <Route path="/GEM/analysis/:surveyid/:sectionid" element={<GEMAnalysis1 />} />
              </Route>

              <Route path="/start-survey/:id/:demoid?" element={<StartMixer />} />
              <Route path="/type1/:id/:sid/:demoid?" element={<FillSurveySingle />} />
              <Route path="/thank-you/:demoid?" element={<ThankYou />} />
              <Route path="/open-visuals/:surveyid/:accessid" element={<OpenVisuals />} />
              {/* <Route path="/new-minimal" element={<MinimalSurveyComponent />} /> */}
              {/* <Route path="/google-forms" element={<GoogleForms />} /> */}
              <Route path="/googled" element={<ThankYou />} />
              {/* <Route path="/new-dash" element={<NewDash />} />
              <Route path="/new-dashboard" element={<NewDashboard />} /> */}
              <Route path="/view-feedback-openai/:surveyid/:filter?/:timekey?" element={<ViewFeedbacksOpenAI />} />
              <Route path="/login" element={<SimpleLoginPage />} />
              <Route path="auth-callback" element={<AuthCallback />} />
              <Route path="auth/callback" element={<AmplifyOAuthCallback />} />
              <Route path="/excel-upload" element={<ExcelUpload />} />
              <Route path="/gradio-embed" element={<GradioInterface />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CSSTransition timeout={330} in={state.isSearchOpen} classNames="search-overlay" unmountOnExit>
              <Search />
            </CSSTransition>
          </Suspense>
        </BrowserRouter>
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

const posthogOptions = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
};

let root = window.__REACT_ROOT__;
if (!root) {
  root = ReactDOM.createRoot(document.querySelector("#app"));
  window.__REACT_ROOT__ = root;
}
root.render(
  <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY} options={posthogOptions}>
    <Main />
  </PostHogProvider>
);

if (module.hot) {
  module.hot.accept();
}
